import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import EventIcon from '@mui/icons-material/Event';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HomeIcon from '@mui/icons-material/Home';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { conectarSocket, type StatusPedidoEvento } from '../../lib/socket';
import { montarLinkInstagram, montarLinkWhatsapp } from '../../lib/contato';
import {
  useGetAvaliacaoQuery,
  useGetLojaAtualQuery,
  useGetPedidoStatusQuery,
  useGetSenhaTotemQuery,
  useImprimirNotaTotemMutation,
  ROTULO_CONSUMO,
  type ConfiguracaoLoja,
  type StatusNotaFiscalTotem,
  type PayloadPedidoDetalhe,
} from '../../api';
import type { RootState } from '../../store';
import { formatarBRL } from '../../lib/formato';
import { emCentavos } from '../../lib/cashback-pedido'
import { registrarPedidoLocal } from '../../lib/pedidos-locais';
import { sinalizarRepouso } from '../../lib/protecao-ociosa';
import { CabecalhoModal } from '../../components/cabecalho-modal';
import { DialogPixEstatico } from '../../components/dialog-pix-estatico';
import { ESTILO_ACAO_KIOSK, TemaKiosk } from '../../components/catalogo-kiosk/tema-kiosk';
import { EsperaTotem, PAUSA_NOTA_TOTEM_MS, PAUSA_SENHA_TOTEM_MS, type FaseEsperaTotem } from './espera-totem';
import { AguardandoPagamento } from './aguardando-pagamento';
import { AvisoNotaFiscalTotem } from '../../components/totem/nota-fiscal-totem';
import { useImpressaoNoAparelho } from './use-impressao-no-aparelho';
import { getLocalImpressaoTotem } from '../../lib/totem-config';
import { impressaoCupomDaNota, opcoesImpressaoTotem, type EtapaImpressaoNota } from '../../lib/impressao-totem';
import PosPagamentoLayout from '../totem/checkout/payment-screen/pos-pagamento/pos-pagamento-layout';
import ImpressaoPergunta from '../totem/checkout/payment-screen/pos-pagamento/impressao-pergunta';
import ScreenHeader from '../totem/checkout/components/screen-header';

/** Polling em ms enquanto situação é PENDENTE. */
const POLLING_MS = 3000;

/** Situações em que o cliente de mesa/comanda pode avaliar (ali não há confirmação da loja). */
const SITUACOES_AVALIAVEIS = ['CONFIRMADO', 'ENVIADO', 'ENTREGUE', 'AGUARDANDO_RETIRADA'];
/** Janela (ms) para avaliar, a partir da criação do pedido (48h). */
const JANELA_AVALIACAO_MS = 48 * 60 * 60 * 1000;
/**
 * Tempo máximo (ms) aguardando o restaurante CONFIRMAR o pedido. Estourado o
 * prazo (PDV fechado, restaurante sem confirmar), paramos de aguardar, exibimos
 * os detalhes do pedido e sugerimos contato direto com a loja.
 */
const TIMEOUT_ESPERA_MS = 300_000;
/** Tempo (s) para totem voltar ao catálogo após confirmação. */
const RETORNO_TOTEM_SEG = 15;
/**
 * Tempo máximo (ms) segurando a tela do TOTEM à espera da senha. Estourado o
 * prazo (fila do Sischef atrasada, PDV fora do ar), liberamos a confirmação com
 * um aviso em vez de prender o cliente no aparelho para sempre.
 */
const TIMEOUT_SENHA_TOTEM_MS = 45_000;
/**
 * Tempo máximo (ms) segurando o cliente à espera da NFC-e. Trinta segundos é o teto
 * do que se pode pedir a alguém parado em pé no totem — passou disso, a tela libera
 * com o aviso de que a nota pode sair depois. A emissão continua no Sischef; o que
 * acaba aqui é a espera.
 */
const TIMEOUT_NOTA_TOTEM_MS = 30_000;
/**
 * Respiro (ms) entre a nota ser enviada para a impressora e a tela liberar — o
 * tempo médio que o cupom leva para sair do papel. Sem ele a tela fechava com o
 * cliente ainda de mãos vazias, e o cupom aparecia com ele já de costas.
 */
const PAUSA_CUPOM_TOTEM_MS = 2_000;
/** Sem resposta à pergunta da nota nesse prazo, segue como "não imprimir" — o totem não fica preso. */
const TIMEOUT_PERGUNTA_NOTA_MS = 30_000;
/** Backend sem responder ao "Sim, imprimir" nesse prazo: segue como impressão que falhou. */
const TIMEOUT_IMPRESSAO_NOTA_MS = 20_000;

/** Etapas exibidas no acompanhamento (estilo print). */
/**
 * Rótulos do acompanhamento. Em pedidos de RETIRADA o 3º passo é "Aguardando
 * retirada" (o pedido fica pronto para o cliente buscar) em vez de "Enviado".
 */
function rotulosEtapas(ehRetirada: boolean): readonly string[] {
  return ['Recebido', 'Confirmado', ehRetirada ? 'Aguardando retirada' : 'Enviado', 'Entregue'];
}

/** Situações que indicam que a integração foi concluída (com ou sem erro). */
function integracaoResolvida(situacao: string): boolean {
  return situacao !== 'PENDENTE';
}

/**
 * Enquanto seguimos CONSULTANDO o status: PENDENTE (ainda sendo enviado à loja)
 * ou ENVIADO (sincronizado com o Sischef, mas o restaurante ainda não reagiu).
 * A partir de CONFIRMADO — ou do despacho — o acompanhamento avança e o polling
 * encerra.
 */
function aguardandoRestaurante(situacao: string, despachado: boolean): boolean {
  if (despachado) return false;
  // Aguardando pagamento também consulta: é assim que a tela vê o PIX pago e passa para a espera da loja.
  return situacao === 'PENDENTE' || situacao === 'ENVIADO' || situacao === 'AGUARDANDO_PAGAMENTO';
}

/**
 * A TELA de espera — exclusiva do DELIVERY. Da ótica do cliente o pedido segue
 * "aguardando o restaurante confirmar" enquanto está PENDENTE (ainda sendo
 * lançado na loja) ou ENVIADO — que aqui é status de INTEGRAÇÃO (sincronizou
 * com o Sischef), NÃO confirmação do restaurante. Só a partir de CONFIRMADO
 * fazem sentido a animação de "Pedido confirmado" e o acompanhamento.
 *
 * Não vale para totem/tablet/mesa/comanda: ali não existe confirmação do
 * restaurante (o pedido cai direto na cozinha/balcão) e a tela confirma já no
 * envio, pelo ramo `ehLocal`.
 */
function aguardandoConfirmacao(situacao: string): boolean {
  return situacao === 'PENDENTE' || situacao === 'ENVIADO';
}

/**
 * Situações em que o restaurante JÁ reagiu ao pedido. Servem de trava de
 * não-retrocesso: o Sischef reusa `ENVIADO` no despacho ("saiu para entrega"),
 * então sem a trava um pedido já confirmado voltaria para a tela de espera.
 */
const SITUACOES_POS_CONFIRMACAO = ['CONFIRMADO', 'EM_PRODUCAO', 'PRONTO', 'AGUARDANDO_RETIRADA', 'ENTREGUE'];

function confirmadoPelaLoja(situacao: string): boolean {
  return SITUACOES_POS_CONFIRMACAO.includes(situacao);
}

/**
 * Mapeia a situação do pedido para o índice da etapa atual no acompanhamento.
 * O mapeamento depende do tipo do pedido porque duas situações têm leitura
 * diferente para o cliente:
 * - ENTREGA + AGUARDANDO_RETIRADA: uso interno (gestão de entregadores — pedido
 *   pronto aguardando o entregador). O cliente continua vendo "Confirmado".
 * - RETIRADA + ENVIADO: "saiu para entrega" não se aplica à retirada; mantém
 *   como "Confirmado" (em vez de voltar visualmente para "Recebido").
 *
 * `despachado` desempata o ENVIADO: sem o carimbo ele é o do checkout ("a loja
 * recebeu"); com o carimbo é o despacho feito no Sischef, que avança a etapa.
 * Ele também é PISO: depois do despacho ainda chega status que mapeia para etapa
 * anterior (AGUARDANDO_RETIRADA do KDS, CONFIRMADO fora de ordem) e sem o piso a
 * tela volta de "Enviado" para "Confirmado". Não remover.
 */
function etapaAtual(situacao: string, ehRetirada: boolean, despachado: boolean): number {
  const etapa = etapaDaSituacao(situacao, ehRetirada);
  return despachado ? Math.max(etapa, ehRetirada ? 1 : 2) : etapa;
}

function etapaDaSituacao(situacao: string, ehRetirada: boolean): number {
  switch (situacao) {
    case 'ENTREGUE':
      return 3;
    case 'AGUARDANDO_RETIRADA':
      return ehRetirada ? 2 : 1;
    case 'CONFIRMADO':
      return 1;
    case 'ENVIADO':
      return ehRetirada ? 1 : 0;
    default:
      return 0; // PENDENTE
  }
}

/** Título do pedido (tipo + número da mesa/comanda quando houver). */
function rotuloTipo(
  origem: string | undefined,
  tipoPedido: string | undefined,
  identificador: PayloadPedidoDetalhe['identificador'],
): string {
  const tipo = identificador?.tipo ?? tipoPedido ?? origem;
  if (tipo === 'DELIVERY' || origem === 'DELIVERY') return 'Delivery';
  if (tipo === 'MESA') return identificador?.numero ? `Mesa ${identificador.numero}` : 'Mesa';
  if (tipo === 'COMANDA') return identificador?.numero ? `Comanda ${identificador.numero}` : 'Comanda';
  if (tipo === 'TOTEM' || origem === 'TOTEM') return 'Totem';
  return 'Pedido';
}

function freteComDescontoDoPedido(
  payload: PayloadPedidoDetalhe | null | undefined,
  ehRetirada: boolean,
): { cheio: number; cobrado: number; conveniencia: number } | null {
  if (ehRetirada || payload?.valorTaxaEntregaOriginal == null) return null
  const conveniencia = payload.valorTaxaConveniencia ?? 0
  return {
    cheio: (emCentavos(payload.valorTaxaEntregaOriginal) - emCentavos(conveniencia)) / 100,
    cobrado: (emCentavos(payload.valorTaxaEntrega ?? 0) - emCentavos(conveniencia)) / 100,
    conveniencia,
  }
}

function montarEndereco(pessoa: PayloadPedidoDetalhe['pessoa']): string | null {
  if (!pessoa) return null;
  const linha = [pessoa.endereco, pessoa.numero].filter(Boolean).join(', ');
  const txt = [linha, pessoa.bairro].filter(Boolean).join(' - ');
  return txt || null;
}

function faixaTempo(config: ConfiguracaoLoja | undefined): string | null {
  if (!config) return null;
  const { tempoMinPreparo: min, tempoMaxPreparo: max } = config;
  if (min != null && max != null) return `${min}m - ${max}m`;
  if (max != null) return `${max}m`;
  if (min != null) return `${min}m`;
  return null;
}

const fmtData = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

export interface PedidoConfirmadoProps {
  idPedido?: string;
  aoEncerrar?: () => void;
  aoAvaliar?: (idPedido: string) => void;
  /** Cancelar que chegou depois do pagamento: o aviso vem na navegação para sobreviver à troca de tela. */
  pagoAoCancelar?: boolean;
}

export function PedidoConfirmadoPage({ idPedido, aoEncerrar, aoAvaliar, pagoAoCancelar = false }: PedidoConfirmadoProps = {}): JSX.Element {
  const id = idPedido;
  const { data: loja } = useGetLojaAtualQuery();
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const theme = useTheme();
  // Desktop: a confirmação abre como modal centralizado; mobile: tela cheia.
  const ehDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const ehTotem = loja?.identificador.tipo === 'TOTEM';
  // Tablet (mesa/comanda): não tem fluxo de status estilo delivery — só confirma o envio.
  const ehTablet = loja?.identificador.tipo === 'TABLET';
  const ehMesaComanda = loja?.identificador.tipo === 'MESA' || loja?.identificador.tipo === 'COMANDA';
  // Modos "locais" (pedido vai pra cozinha/balcão): sem acompanhamento de status
  // estilo delivery e sem infos de entrega/retirada. Só confirmam o envio.
  const ehLocal = ehTotem || ehTablet || ehMesaComanda;
  // Login/conta só existe no MODO delivery (pelo identificador da loja). NÃO usar o
  // ehDelivery por pedido aqui: um totem "levar" pode vir com tipoPedido=DELIVERY.
  const ehModoDelivery = loja?.identificador.tipo === 'DELIVERY';

  // Polling: ativo enquanto situação for PENDENTE
  const [pollingAtivo, setPollingAtivo] = useState(true);
  const { data: pedido } = useGetPedidoStatusQuery(id ?? '', {
    skip: !id,
    pollingInterval: pollingAtivo ? POLLING_MS : 0,
  });

  // Sem login, o aparelho guarda a referência do pedido: é o que o cliente reabre pelo cardápio.
  const idConfiguracao = loja?.configuracao.id;
  useEffect(() => {
    if (accessToken || !ehModoDelivery || idConfiguracao == null || pedido?.origem !== 'DELIVERY') return;
    registrarPedidoLocal(idConfiguracao, pedido);
  }, [accessToken, ehModoDelivery, idConfiguracao, pedido]);

  const opcoesImpressao = opcoesImpressaoTotem(loja?.configuracao);
  // Loja que emite NFC-e no totem: a nota sai depois do pedido e o cliente precisa
  // saber se ela saiu — a mesma consulta da senha traz o acompanhamento.
  const ehNfceTotem = ehTotem && opcoesImpressao.nota !== 'NAO';
  // "Perguntar ao cliente": a nota é emitida do mesmo jeito; autorizada, ele escolhe se sai no papel.
  const perguntaNota = ehTotem && opcoesImpressao.nota === 'PERGUNTAR';

  // Senha do totem (= sequencial do Sischef) + status da NFC-e. Poll curto até a
  // senha aparecer e, quando há nota, até ela resolver (autorizada ou erro).
  const [senha, setSenha] = useState<number | null>(null);
  const [statusNota, setStatusNota] = useState<StatusNotaFiscalTotem | null>(null);
  const [esperaNotaEsgotada, setEsperaNotaEsgotada] = useState(false);

  const notaResolvida =
    statusNota?.situacao === 'AUTORIZADA' ||
    statusNota?.situacao === 'ERRO' ||
    (ehNfceTotem && loja?.configuracao.totemEmissorNfceConfigurado === false);
  const acompanhandoNota = ehNfceTotem && !notaResolvida && !esperaNotaEsgotada;

  const { data: senhaResp } = useGetSenhaTotemQuery(id ?? '', {
    skip: !id || !ehTotem || (senha != null && !acompanhandoNota),
    pollingInterval: 2500,
  });
  useEffect(() => {
    if (senhaResp?.sequencial != null) setSenha(senhaResp.sequencial);
    if (senhaResp?.notaFiscal) setStatusNota(senhaResp.notaFiscal);
  }, [senhaResp]);

  // Desiste do acompanhamento fiscal no prazo (ver TIMEOUT_NOTA_TOTEM_MS).
  useEffect(() => {
    if (!ehNfceTotem || notaResolvida) return;
    const timer = setTimeout(() => setEsperaNotaEsgotada(true), TIMEOUT_NOTA_TOTEM_MS);
    return () => clearTimeout(timer);
  }, [ehNfceTotem, notaResolvida]);

  /*
   * Loja com NFC-e ligada e sem emissor configurado no backoffice: a nota não vai ser
   * emitida nunca, e o Sischef só diria isso depois de processar o pedido. Como a
   * configuração já é conhecida aqui, o erro é montado na hora — o cliente não fica
   * 30s esperando um cupom que ninguém vai emitir.
   */
  const semEmissorNfce = ehNfceTotem && loja?.configuracao.totemEmissorNfceConfigurado === false;
  const statusNotaEfetivo: StatusNotaFiscalTotem | null = semEmissorNfce
    ? { situacao: 'ERRO', numero: null, mensagem: 'Usuário emissor da NFC-e não configurado no cardápio.' }
    : statusNota;

  // Contagem visível da espera fiscal — quem está parado na frente do totem precisa
  // saber que a espera tem fim, e quanto falta dele.
  const [restanteNota, setRestanteNota] = useState(Math.round(TIMEOUT_NOTA_TOTEM_MS / 1000));
  useEffect(() => {
    if (!ehNfceTotem || notaResolvida || semEmissorNfce) return;
    const intervalo = window.setInterval(() => setRestanteNota((r) => Math.max(0, r - 1)), 1000);
    return () => window.clearInterval(intervalo);
  }, [ehNfceTotem, notaResolvida, semEmissorNfce]);

  // Impressão da nota em "Perguntar ao cliente". A trava é ref e não volta: cada chamada é
  // uma nota no papel, e o servidor de impressão não deduplica — dois toques, duas notas.
  const [etapaImpressaoNota, setEtapaImpressaoNota] = useState<EtapaImpressaoNota>('PENDENTE');
  const respostaNotaDada = useRef(false);
  const [imprimirNota] = useImprimirNotaTotemMutation();

  const aoImprimirNota = useCallback(() => {
    if (!id || respostaNotaDada.current) return;
    respostaNotaDada.current = true;
    setEtapaImpressaoNota('ENVIANDO');
    // Resposta que chega depois do prazo (ver TIMEOUT_IMPRESSAO_NOTA_MS) não reabre a espera.
    const concluir = (etapa: EtapaImpressaoNota): void =>
      setEtapaImpressaoNota((atual) => (atual === 'ENVIANDO' ? etapa : atual));
    imprimirNota({ id, localImpressao: getLocalImpressaoTotem().trim() || null })
      .unwrap()
      .then((r) => {
        if (!r.impressa) console.warn('[nfce-totem] nota não impressa', r.mensagem);
        concluir(r.impressa ? 'ENVIADA' : 'FALHOU');
      })
      .catch((err: unknown) => {
        // Tela recarregada depois de já ter impresso: o servidor só imprime uma vez por pedido.
        const codigo = (err as { data?: { codigo?: string } } | null)?.data?.codigo;
        if (codigo === 'NOTA_TOTEM_JA_IMPRESSA') return concluir('ENVIADA');
        console.error('[nfce-totem] falha ao pedir a impressão da nota', err);
        concluir('FALHOU');
      });
  }, [id, imprimirNota]);

  useEffect(() => {
    if (etapaImpressaoNota !== 'ENVIANDO') return;
    const timer = setTimeout(() => setEtapaImpressaoNota('FALHOU'), TIMEOUT_IMPRESSAO_NOTA_MS);
    return () => clearTimeout(timer);
  }, [etapaImpressaoNota]);

  const aoPularNota = useCallback(() => {
    if (respostaNotaDada.current) return;
    respostaNotaDada.current = true;
    setEtapaImpressaoNota('RECUSADA');
  }, []);

  // Cupom enviado à impressora: na nota SEMPRE é a própria autorização; em PERGUNTAR, a
  // impressão que o cliente pediu. Ainda falta ele sair do papel (ver PAUSA_CUPOM_TOTEM_MS).
  // Erro não passa por aqui: libera na hora, sem papel a esperar.
  const cupomEnviado =
    statusNota?.situacao === 'AUTORIZADA' && (!perguntaNota || etapaImpressaoNota === 'ENVIADA');
  const [cupomLiberado, setCupomLiberado] = useState(false);
  useEffect(() => {
    if (!cupomEnviado) return;
    const timer = setTimeout(() => setCupomLiberado(true), PAUSA_CUPOM_TOTEM_MS);
    return () => clearTimeout(timer);
  }, [cupomEnviado]);

  // TOTEM Android: a impressora é do aparelho — senha, resumo e nota saem daqui.
  const falhouImpressaoNoAparelho = useImpressaoNoAparelho({
    idPedido: id,
    ehTotem,
    opcoes: opcoesImpressao,
  });

  // Desiste de segurar a tela se a senha não vier no prazo (ver TIMEOUT_SENHA_TOTEM_MS).
  const [esperaSenhaEsgotada, setEsperaSenhaEsgotada] = useState(false);
  useEffect(() => {
    if (!ehTotem || senha != null) return;
    const timer = setTimeout(() => setEsperaSenhaEsgotada(true), TIMEOUT_SENHA_TOTEM_MS);
    return () => clearTimeout(timer);
  }, [ehTotem, senha]);

  // A senha fica sozinha na tela antes da nota ou da pergunta do cupom (ver PAUSA_SENHA_TOTEM_MS).
  const [senhaApresentada, setSenhaApresentada] = useState(false);
  useEffect(() => {
    if (!ehTotem || senha == null) return;
    const timer = setTimeout(() => setSenhaApresentada(true), PAUSA_SENHA_TOTEM_MS);
    return () => clearTimeout(timer);
  }, [ehTotem, senha]);
  const senhaJaVista = senha == null || senhaApresentada;

  // Se este pedido já foi avaliado por este cliente (oculta o botão "Avalie").
  const { data: avaliacaoAtual } = useGetAvaliacaoQuery(id ?? '', { skip: !id });

  // Situação local: começa como PENDENTE; atualizada pelo polling e pelo socket
  const [situacao, setSituacao] = useState<string>('PENDENTE');
  // Pedido já despachado pelo Sischef (o ENVIADO "tardio"). Só avança, nunca volta.
  const [despachado, setDespachado] = useState(false);

  // Trava de não-retrocesso da confirmação (ver SITUACOES_POS_CONFIRMACAO):
  // uma vez confirmado, a tela não volta para a espera.
  const [jaConfirmado, setJaConfirmado] = useState(false);
  const [pixAberto, setPixAberto] = useState(false);
  useEffect(() => {
    if (confirmadoPelaLoja(situacao)) setJaConfirmado(true);
  }, [situacao]);

  // Quando parar o polling:
  // - Totem (kiosk): assim que a integração resolve (sincronizou ou falhou) —
  //   a sessão é efêmera e retorna ao catálogo logo em seguida.
  // - Demais canais: o cliente fica acompanhando no próprio aparelho, então
  //   seguimos buscando até o restaurante CONFIRMAR (ou cancelar/falhar).
  useEffect(() => {
    if (!pedido) return;
    const jaDespachado = pedido.payloadJson?.despachadoEm != null;
    // Resposta emitida ANTES do despacho não pode desfazer o que o socket já entregou.
    if (despachado && !jaDespachado) return;
    setSituacao(pedido.situacao);
    if (jaDespachado) setDespachado(true);
    const parar = ehTotem ? integracaoResolvida(pedido.situacao) : !aguardandoRestaurante(pedido.situacao, jaDespachado);
    if (parar) setPollingAtivo(false);
  }, [pedido, ehTotem]);

  // Espera esgotada: após TIMEOUT_ESPERA_MS ainda aguardando confirmação,
  // desistimos da espera ativa e caímos na tela de detalhes + contato. Não
  // aplica ao totem (kiosk volta ao catálogo sozinho).
  const [esperaEsgotada, setEsperaEsgotada] = useState(false);
  const timerEsperaRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // O prazo da loja só corre com ela esperando: aguardando pagamento ainda não chegou lá.
  const esperandoLoja = aguardandoConfirmacao(situacao);

  useEffect(() => {
    if (!pollingAtivo || ehTotem || !esperandoLoja) {
      if (timerEsperaRef.current) clearTimeout(timerEsperaRef.current);
      return;
    }
    timerEsperaRef.current = setTimeout(() => {
      setEsperaEsgotada(true);
      setPollingAtivo(false);
    }, TIMEOUT_ESPERA_MS);
    return () => {
      if (timerEsperaRef.current) clearTimeout(timerEsperaRef.current);
    };
  }, [pollingAtivo, ehTotem, esperandoLoja]);

  // Socket.IO: atualizações de status após a integração (para usuários logados)
  useEffect(() => {
    if (!accessToken) return;
    const socket = conectarSocket(accessToken);
    const handler = (evento: StatusPedidoEvento): void => {
      if (String(evento.idPedidoCardapio) === id) {
        // Todo evento daqui nasce no webhook do Sischef, e só manda
        // ENVIADO ao DESPACHAR — o ENVIADO do checkout não passa por este canal.
        const despachouAgora = evento.status === 'ENVIADO';
        if (despachouAgora) setDespachado(true);
        setSituacao(evento.status);
        const parar = ehTotem ? integracaoResolvida(evento.status) : !aguardandoRestaurante(evento.status, despachouAgora);
        if (parar) setPollingAtivo(false);
      }
    };
    socket.on('status-pedido', handler);
    return () => {
      socket.off('status-pedido', handler);
    };
  }, [accessToken, id, ehTotem]);

  // TOTEM/TABLET: contador de retorno automático ao catálogo, liberando o
  // aparelho da mesa/balcão para o próximo cliente após a confirmação.
  const [restanteRetorno, setRestanteRetorno] = useState(RETORNO_TOTEM_SEG);

  /**
   * TOTEM: a tela fica presa até o cliente ter tudo em mãos.
   *
   * Primeiro a SENHA — único comprovante que ele leva daqui, e a confirmação antiga
   * já mostrava o botão de novo pedido com o contador correndo, então quem saísse
   * antes do sequencial chegar não tinha por qual número esperar. Depois, na loja que
   * emite NFC-e, o cupom: a nota sai alguns segundos depois da senha, e fechar a tela
   * no meio fazia o cliente ir embora achando que a impressão falhou.
   *
   * Falha de envio, cancelamento e erro na nota liberam na hora — nesses casos não vem
   * papel nenhum, e segurar o cliente à toa é pior que soltar.
   */
  const aguardandoSenhaTotem =
    ehTotem &&
    senha == null &&
    !esperaSenhaEsgotada &&
    situacao !== 'FALHA_ENVIO' &&
    situacao !== 'CANCELADO';

  // Em PERGUNTAR, "não" (ou impressão que falhou) libera na hora: não há papel a esperar.
  const aguardandoCupomTotem =
    ehNfceTotem &&
    !esperaNotaEsgotada &&
    situacao !== 'FALHA_ENVIO' &&
    situacao !== 'CANCELADO' &&
    statusNotaEfetivo?.situacao !== 'ERRO' &&
    !(
      statusNotaEfetivo?.situacao === 'AUTORIZADA' &&
      ((cupomLiberado && senhaJaVista) || etapaImpressaoNota === 'RECUSADA' || etapaImpressaoNota === 'FALHOU')
    );

  // A tela da nota só abre se a nota ainda estiver pendente quando a senha sai: resolvida antes, ela só piscaria.
  const [notaNaTela, setNotaNaTela] = useState(false);
  useEffect(() => {
    if (senhaApresentada && aguardandoCupomTotem && !notaResolvida) setNotaNaTela(true);
  }, [senhaApresentada, aguardandoCupomTotem, notaResolvida]);

  // Aberta, ela fica um mínimo antes de dar lugar à pergunta ou à confirmação (ver PAUSA_NOTA_TOTEM_MS).
  const [notaApresentada, setNotaApresentada] = useState(false);
  useEffect(() => {
    if (!notaNaTela) return;
    const timer = setTimeout(() => setNotaApresentada(true), PAUSA_NOTA_TOTEM_MS);
    return () => clearTimeout(timer);
  }, [notaNaTela]);
  const segurandoNota = notaNaTela && !notaApresentada;

  const aguardandoTotem = aguardandoSenhaTotem || aguardandoCupomTotem || segurandoNota;
  // Senha que não veio no prazo não tem tela própria; respondida a pergunta do cupom, o que resta é a nota.
  const faseEspera: FaseEsperaTotem = aguardandoSenhaTotem
    ? 'REGISTRANDO'
    : senha == null || notaNaTela || etapaImpressaoNota !== 'PENDENTE'
      ? 'NOTA'
      : 'SENHA';
  const impressaoCupom = impressaoCupomDaNota(opcoesImpressao.nota, etapaImpressaoNota, aguardandoTotem);

  // Só com a nota AUTORIZADA: erro, sem emissor ou SEFAZ fora do prazo seguem sem pergunta.
  // E só depois de a senha ter ficado na tela: a pergunta não pode cortar o número.
  const mostrarPerguntaNota =
    perguntaNota &&
    senhaJaVista &&
    !segurandoNota &&
    statusNotaEfetivo?.situacao === 'AUTORIZADA' &&
    etapaImpressaoNota === 'PENDENTE' &&
    !esperaNotaEsgotada &&
    situacao !== 'FALHA_ENVIO' &&
    situacao !== 'CANCELADO';

  useEffect(() => {
    if (!mostrarPerguntaNota) return;
    const timer = setTimeout(aoPularNota, TIMEOUT_PERGUNTA_NOTA_MS);
    return () => clearTimeout(timer);
  }, [mostrarPerguntaNota, aoPularNota]);

  const ehConfirmadoTotem =
    ehTotem && situacao !== 'PENDENTE' && situacao !== 'FALHA_ENVIO' && !aguardandoTotem;
  const ehConfirmadoTablet = ehTablet && situacao !== 'FALHA_ENVIO' && situacao !== 'CANCELADO';
  const ehRetornoAutomatico = ehConfirmadoTotem || ehConfirmadoTablet;

  useEffect(() => {
    if (!ehRetornoAutomatico) return;
    const intervalo = window.setInterval(() => setRestanteRetorno((r) => Math.max(0, r - 1)), 1000);
    return () => window.clearInterval(intervalo);
  }, [ehRetornoAutomatico]);

  /**
   * Volta ao cardápio encerrando a sessão quando for o caso. No TOTEM o pedido
   * FECHA o atendimento — o próximo cliente tem que encontrar o convite, não o
   * catálogo de quem acabou de pedir. No TABLET não: a mesa continua comendo e
   * pode pedir de novo, então lá vale só o relógio de ociosidade.
   */
  const voltarAoCatalogo = useCallback(() => {
    aoEncerrar?.();
    if (ehTotem) sinalizarRepouso();
  }, [aoEncerrar, ehTotem]);

  useEffect(() => {
    if (!ehRetornoAutomatico || restanteRetorno !== 0) return;
    voltarAoCatalogo();
    // Ninguém tocou: no tablet a proteção entra já. Quem toca em "Fazer outro pedido" quer o cardápio.
    if (ehTablet) sinalizarRepouso();
  }, [ehRetornoAutomatico, restanteRetorno, voltarAoCatalogo, ehTablet]);

  const fechar = (): void => {
    aoEncerrar?.();
  };

  // Envolve o conteúdo no modal. No TOTEM é tela cheia com o cabeçalho das outras telas dele (o modal de
  // delivery virava uma caixinha no meio de 1080×1920); sem voltar: o retorno é automático.
  const comoModal = (corpo: JSX.Element, totem: { subtitulo?: string; rodape?: ReactNode } = {}): JSX.Element => ehTotem ? (
    <Stack sx={{ width: '100%', height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' }, bgcolor: 'background.default' }}>
      <ScreenHeader title="Seu pedido" subTitle={totem.subtitulo ?? ''} logoUrl={loja?.configuracao.urlLogo ?? null} />

      <TemaKiosk tambemNoTotem>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', px: 3, py: 4 }}>
          <Box sx={{ width: '100%', maxWidth: 760, m: 'auto' }}>{corpo}</Box>
        </Box>

        {totem.rodape && (
          <Box sx={{ flexShrink: 0, p: 2, pb: 'calc(16px + env(safe-area-inset-bottom))', borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Box sx={{ maxWidth: 760, mx: 'auto' }}>{totem.rodape}</Box>
          </Box>
        )}
      </TemaKiosk>
    </Stack>
  ) : (
    <Dialog
      open
      onClose={ehTotem ? undefined : fechar}
      disableEscapeKeyDown={ehTotem}
      fullScreen={!ehDesktop}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{ sx: { position: 'relative', ...(ehDesktop ? { maxHeight: '90vh' } : {}) } }}
    >
      <CabecalhoModal titulo="Seu pedido" aoFechar={ehTotem ? undefined : fechar} />
      <DialogContent sx={{ pt: 2, pb: 4 }}>{corpo}</DialogContent>
    </Dialog>
  );

  // Enquanto o pedido não chega (primeira carga), mostra um spinner.
  if (!pedido) {
    return comoModal(
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>,
    );
  }

  const ehFalha = situacao === 'FALHA_ENVIO';
  const ehCancelado = situacao === 'CANCELADO';
  // Sinal do backend, não o texto do motivo: CANCELED só nasce de cancelamento sem pagamento.
  const canceladoAntesDoPagamento = ehCancelado && pedido.statusPagamento === 'CANCELED';
  const avisoPagoAoCancelar = pagoAoCancelar ? (
    <Alert severity="info">Seu pagamento já tinha sido aprovado. O pedido não foi cancelado e já foi para a loja.</Alert>
  ) : null;

  // Delivery só pede avaliação depois que a LOJA confirmou: o ENVIADO do checkout é
  // status de integração, e com ele o botão aparecia ainda em "Aguardando confirmação".
  const confirmadoParaAvaliar = ehModoDelivery
    ? (jaConfirmado || despachado || confirmadoPelaLoja(situacao)) && !ehFalha && !ehCancelado
    : SITUACOES_AVALIAVEIS.includes(situacao);
  // "Avalie o pedido": só se NÃO for totem, já confirmado, dentro de 48h e ainda não avaliado.
  const podeAvaliar =
    !ehTotem &&
    confirmadoParaAvaliar &&
    avaliacaoAtual?.avaliacao == null &&
    !!pedido?.dataCriacao &&
    Date.now() - new Date(pedido.dataCriacao).getTime() < JANELA_AVALIACAO_MS;
  // Espera ativa: SÓ no modo delivery, único canal em que a confirmação do
  // restaurante é um passo real do fluxo. Totem/tablet/mesa/comanda caem no
  // ramo `ehLocal` e confirmam no envio; o kiosk volta ao catálogo sozinho.
  const aguardando = ehModoDelivery && !jaConfirmado && aguardandoConfirmacao(situacao);
  // Pix direto para a loja: só depois que ela confirmou — antes o pedido ainda pode ser recusado.
  const pixEstatico =
    ehModoDelivery && (jaConfirmado || despachado || confirmadoPelaLoja(situacao)) && !ehFalha && !ehCancelado
      ? pedido.pix ?? null
      : null;

  const payload = pedido.payloadJson;
  // Retirada: pedido de totem/tablet OU delivery em que o cliente optou por retirar
  // (payload.tipoDelivery = 'RETIRAR_LOCAL'). Altera os rótulos das etapas e o resumo
  // (sem "Enviado" nem endereço de entrega).
  const ehRetirada = !ehModoDelivery || payload?.tipoDelivery === 'RETIRAR_LOCAL';
  const config = loja?.configuracao;
  const endereco = montarEndereco(payload?.pessoa);
  const formaPagamento = payload?.pagamentos?.[0]?.formaPagamento?.descricao;
  const total = payload?.valorTotal;
  // Pedido antigo não tem a chave e pedido que não usou tem `null`: os dois valem zero.
  const cashbackUsado = payload?.cashback?.valorUsado ?? 0
  const tempo = faixaTempo(config);
  const itens = payload?.itens ?? [];
  const referencia = pedido.shortId ?? id?.slice(0, 8).toUpperCase();
  const consumoTotem = payload?.tipoDelivery === 'CONSUMIR_LOCAL'
    ? ROTULO_CONSUMO.LOCAL
    : payload?.tipoDelivery === 'RETIRAR_LOCAL' ? ROTULO_CONSUMO.LEVAR : null;
  // Toda a sequência do totem (espera, pergunta da nota, confirmação) usa o mesmo cabeçalho.
  const subtituloTotem = [`Pedido #${referencia}`, consumoTotem].filter(Boolean).join(' · ');

  /* Nota em "Perguntar ao cliente": mesma pergunta (e mesma tela) do totem de checkout. */
  if (mostrarPerguntaNota) {
    return (
      <Stack sx={{ width: '100%', height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' }, bgcolor: 'background.default' }}>
        <ScreenHeader title="Seu pedido" subTitle={subtituloTotem} logoUrl={loja?.configuracao.urlLogo ?? null} />

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex' }}>
          <PosPagamentoLayout valorFormatado={total != null ? formatarBRL(total) : ''} titulo="Pedido confirmado!" embutido>
            <ImpressaoPergunta onImprimir={aoImprimirNota} onPular={aoPularNota} />
          </PosPagamentoLayout>
        </Box>
      </Stack>
    );
  }

  /* TOTEM: segura a tela até senha e cupom — ver aguardandoTotem. */
  if (aguardandoTotem) {
    return (
      <TemaKiosk>
        <EsperaTotem
          fase={faseEspera}
          impressaoCupom={impressaoCupom}
          statusNota={statusNotaEfetivo}
          contagem={{ restante: restanteNota, total: Math.round(TIMEOUT_NOTA_TOTEM_MS / 1000) }}
          senha={senha}
          senhaImpressa={opcoesImpressao.imprimirSenha}
          referenciaPedido={referencia}
          cabecalho={{ subtitulo: subtituloTotem, logoUrl: loja?.configuracao.urlLogo ?? null }}
        />
      </TemaKiosk>
    );
  }

  /**
   * TABLET: a mesa não precisa de comprovante.
   *
   * A tela cheia de delivery (status, tempo de entrega, lista de itens,
   * pagamento, total) empurrava o botão de voltar para fora da tela num
   * aparelho de mesa. Aqui fica só o que interessa a quem está sentado: deu
   * certo, foi para a cozinha, e como pedir de novo. Quem quiser conferir o
   * consumo usa "ver total consumido", que é a tela certa para isso.
   */
  if (ehTablet && !ehFalha && !ehCancelado) {
    return (
      <TemaKiosk>
        <Dialog open fullScreen onClose={fechar} PaperProps={{ sx: { bgcolor: 'background.default' } }}>
          <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center', gap: 3, p: 4, textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 96 }} />

            <Box>
              <Typography variant="h3" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                Pedido enviado para a cozinha
              </Typography>

              <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                Você pode continuar pedindo nesta mesa.
              </Typography>
            </Box>

            <Typography variant="body1" color="text.secondary">
              Pedido #{pedido.shortId ?? id?.slice(0, 8).toUpperCase()}
              {total != null ? ` · ${formatarBRL(total)}` : ''}
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={voltarAoCatalogo}
              sx={{ ...ESTILO_ACAO_KIOSK, px: 6 }}
            >
              Fazer outro pedido ({restanteRetorno}s)
            </Button>
          </Stack>
        </Dialog>
      </TemaKiosk>
    );
  }

  // Delivery não pago ainda não foi para a loja: nada de check verde nem acompanhamento.
  if (pedido.origem === 'DELIVERY' && situacao === 'AGUARDANDO_PAGAMENTO') {
    return comoModal(
      <Stack spacing={2.5}>
        {avisoPagoAoCancelar}
        <AguardandoPagamento pedido={pedido} referencia={referencia} />
      </Stack>,
    );
  }

  // Antes da confirmação da loja a tela mostra só a espera: itens, pagamento e
  // entrega voltam quando a loja confirma (ou quando a espera esgota e o cliente
  // precisa dos dados para falar com ela).
  const mostrarDetalhes = !aguardando || esperaEsgotada;
  const etapa = etapaAtual(situacao, ehRetirada, despachado);
  const rotuloModalidade = ehMesaComanda || ehTotem
    ? rotuloTipo(pedido.origem, payload?.tipoPedido, payload?.identificador)
    : ehRetirada ? 'Retirada' : 'Entrega';
  const taxaEntrega = !ehRetirada && payload?.valorTaxaEntrega ? payload.valorTaxaEntrega : null;
  const freteComDesconto = freteComDescontoDoPedido(payload, ehRetirada)
  const acaoNovoPedido = ehRetornoAutomatico ? (
    <Button variant="contained" size="large" fullWidth onClick={voltarAoCatalogo} sx={ehTotem ? ESTILO_ACAO_KIOSK : undefined}>
      {ehTablet ? 'Fazer outro pedido' : 'Fazer novo pedido'} ({restanteRetorno}s)
    </Button>
  ) : null;
  const acaoFalha = (ehTotem || ehTablet) && ehFalha ? (
    <Button variant="outlined" size="large" fullWidth onClick={voltarAoCatalogo} sx={ehTotem ? ESTILO_ACAO_KIOSK : undefined}>
      Voltar ao cardápio
    </Button>
  ) : null;

  return comoModal(
    <Stack spacing={2.5}>
      {/* Topo: estado do pedido em destaque */}
      {ehFalha || ehCancelado ? (
        <HeroStatus
          icone={<Box sx={{ fontSize: 56, lineHeight: 1 }}>{ehCancelado ? '✕' : '!'}</Box>}
          tom={ehCancelado ? 'warning' : 'error'}
          titulo={ehCancelado ? 'Pedido cancelado' : 'Não conseguimos enviar seu pedido'}
          subtitulo={
            ehCancelado
              ? (canceladoAntesDoPagamento && pedido.ultimoErroEnvio) || 'Este pedido foi cancelado.'
              : 'Seu pedido foi salvo, mas não chegou à loja. Fale com o restaurante.'
          }
        />
      ) : aguardando && !esperaEsgotada ? (
        <AguardandoConfirmacao />
      ) : aguardando && esperaEsgotada ? (
        <HeroStatus
          icone={<Box sx={{ fontSize: 56, lineHeight: 1 }}>!</Box>}
          tom="warning"
          titulo="O restaurante ainda não confirmou"
          subtitulo="Ele pode estar fechado ou com muitos pedidos. Seu pedido foi registrado — confirme direto com a loja."
        />
      ) : ehLocal ? (
        <HeroStatus icone={<IconeConfirmado />} titulo="Pedido enviado" subtitulo="Já está com a cozinha." />
      ) : (
        <HeroStatus icone={<IconeConfirmado />} {...textoDaEtapa(etapa, ehRetirada)} />
      )}

      {!ehTotem && <IdentificacaoPedido referencia={referencia} modalidade={rotuloModalidade} />}

      {avisoPagoAoCancelar}

      {ehFalha && pedido.ultimoErroEnvio && (
        <Typography variant="caption" color="text.disabled" align="center">
          Detalhe técnico: {pedido.ultimoErroEnvio}
        </Typography>
      )}

      {!ehLocal && !ehFalha && !ehCancelado && !aguardando && (
        <Secao>
          <LinhaDoTempo etapa={etapa} ehRetirada={ehRetirada} />
        </Secao>
      )}

      {ehTotem && senha != null && (
        <Box sx={{ textAlign: 'center', py: 3, border: 2, borderColor: 'primary.main', borderRadius: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Sua senha
          </Typography>

          <Typography variant="h2" fontWeight={800} color="primary.main" sx={{ lineHeight: 1, fontSize: '96px', my: 1 }}>
            {senha}
          </Typography>

          <Typography variant="body1" color="text.secondary">
            Retire seu pedido quando for chamado.
          </Typography>
        </Box>
      )}

      {ehTotem && falhouImpressaoNoAparelho && !ehFalha && !ehCancelado && (
        <Alert severity="warning">
          Não foi possível imprimir neste totem. Chame um atendente.
        </Alert>
      )}

      {ehTotem && senha == null && esperaSenhaEsgotada && !ehFalha && !ehCancelado && (
        <Alert severity="warning">
          Seu pedido foi registrado, mas a senha ainda não apareceu. Retire o comprovante
          impresso ou informe o número do pedido no balcão.
        </Alert>
      )}

      {ehNfceTotem && !ehFalha && !ehCancelado && (
        <AvisoNotaFiscalTotem
          status={statusNotaEfetivo}
          impressao={impressaoCupom}
          comSenha={opcoesImpressao.imprimirSenha}
        />
      )}

      {/* Cancelado antes de pagar: o pedido nunca chegou à loja, não há o que falar com ela. */}
      {(ehFalha || (ehCancelado && !canceladoAntesDoPagamento) || (esperaEsgotada && !despachado)) && (
        <ContatoRestaurante config={config} referencia={pedido.shortId ?? id} />
      )}

      {pixEstatico && total != null && referencia && (
        <>
          <ConvitePix valor={total} aoAbrir={() => setPixAberto(true)} />
          <DialogPixEstatico
            aberto={pixAberto}
            aoFechar={() => setPixAberto(false)}
            copiaECola={pixEstatico.copiaECola}
            valor={total}
            referencia={referencia}
            whatsapp={config?.whatsapp}
            instagram={config?.instagram}
          />
        </>
      )}

      {podeAvaliar && aoAvaliar && id && <ConviteAvaliacao aoAvaliar={() => aoAvaliar(id)} />}

      {mostrarDetalhes && (
        <>
          <Secao titulo={ehMesaComanda ? 'Pedido' : ehRetirada ? 'Retirada' : 'Entrega'}>
            <Stack spacing={4} sx={{ mt: 3 }}>
              {!ehMesaComanda && !ehFalha && !ehCancelado && tempo && (
                <InfoLinha
                  icone={<ScheduleIcon fontSize="small" />}
                  titulo={`${tempo}`}
                  texto={ehRetirada ? 'Tempo estimado para retirada' : 'Tempo estimado de entrega'}
                />
              )}

              {!ehMesaComanda && (
                ehRetirada ? (
                  <InfoLinha icone={<StorefrontIcon fontSize="small" />} titulo="Retirar na loja" texto={config?.nome} />
                ) : endereco ? (
                  <InfoLinha icone={<HomeIcon fontSize="small" />} titulo="Endereço de entrega" texto={endereco} />
                ) : (
                  <InfoLinha icone={<TwoWheelerIcon fontSize="small" />} titulo="Entrega no endereço informado" />
                )
              )}

              <InfoLinha
                icone={<EventIcon fontSize="small" />}
                titulo="Pedido realizado"
                texto={fmtData.format(new Date(pedido.dataCriacao))}
              />
            </Stack>
          </Secao>

          {/* Itens + pagamento + total */}
          {(itens.length > 0 || total != null) && (
            <Secao titulo="Resumo">
              <Stack spacing={4} sx={{ mt: 3 }}>
                {itens.map((item, i) => (
                  <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        minWidth: 32,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        typography: 'caption',
                        fontWeight: 700,
                        textAlign: 'center',
                      }}
                    >
                      {item.quantidade ?? 1}x
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body1" fontWeight={600}>
                        {item.nome}
                      </Typography>

                      {item.subItens?.map((sub, j) => (
                        <Typography key={j} variant="caption" color="text.secondary" display="block">
                          {sub.quantidade ?? 1}x {sub.nome}
                        </Typography>
                      ))}
                    </Box>
                  </Stack>
                ))}
              </Stack>

              {(formaPagamento || freteComDesconto || taxaEntrega != null || total != null) && (
                <>
                  <Divider sx={{ my: 4 }} />

                  <Stack spacing={1}>
                    {formaPagamento && (
                      <LinhaValor rotulo="Pagamento" valor={formaPagamento} />
                    )}

                    {freteComDesconto && (
                      <LinhaValor
                        rotulo="Taxa de entrega"
                        valor={
                          <>
                            <Box component="s" sx={{ color: 'text.secondary' }}>{formatarBRL(freteComDesconto.cheio)}</Box>{' '}
                            {freteComDesconto.cobrado > 0 ? formatarBRL(freteComDesconto.cobrado) : 'Grátis'}
                          </>
                        }
                      />
                    )}

                    {freteComDesconto && freteComDesconto.conveniencia > 0 && (
                      <LinhaValor rotulo="Taxa de conveniência" valor={formatarBRL(freteComDesconto.conveniencia)} />
                    )}

                    {!freteComDesconto && taxaEntrega != null && (
                      <LinhaValor rotulo="Taxa de entrega" valor={formatarBRL(taxaEntrega)} />
                    )}

                    {cashbackUsado > 0 && <LinhaValor rotulo="Cashback usado" valor={formatarBRL(-cashbackUsado)} />}

                    {total != null && (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant={'h4'}
                          fontWeight='bold'
                          color="success"
                        >
                          Total
                        </Typography>

                        <Typography variant='h4' fontWeight='bold' color="success" sx={{ textAlign: 'right' }}>
                          {formatarBRL(total)}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </>
              )}
            </Secao>
          )}
        </>
      )
      }

      {/* TOTEM/TABLET: instruções + countdown de retorno ao catálogo */}
      {
        ehRetornoAutomatico && (
          <Box>
            <Alert severity="info" sx={{ mb: ehTotem ? 0 : 2 }}>
              {ehTablet
                ? 'Pedido enviado à cozinha! Você pode continuar pedindo nesta mesa.'
                : opcoesImpressao.imprimirSenha
                  ? 'Retire sua senha e aguarde, acompanhe o preparo no balcão.'
                  : 'Guarde o número da sua senha e aguarde, acompanhe o preparo no balcão.'}
            </Alert>
            {/* No totem o botão fica na barra de baixo, como nas outras telas dele. */}
            {!ehTotem && acaoNovoPedido}
          </Box>
        )
      }
      {!ehTotem && acaoFalha}

      {/* Login só existe no MODO delivery: aviso de "não logado" só faz sentido lá
            (gateado pelo identificador da loja, não pelo tipo do pedido). */}
      {
        ehModoDelivery && !accessToken && !ehFalha && !aguardando && (
          <Typography variant="caption" color="text.secondary" align="center">
            Você não está logado — acompanhe o andamento diretamente com a loja.
          </Typography>
        )
      }
    </Stack >,
    { subtitulo: subtituloTotem, rodape: acaoNovoPedido ?? acaoFalha },
  );
}

/** Título e frase do topo conforme a etapa do acompanhamento. */
function textoDaEtapa(etapa: number, ehRetirada: boolean): { titulo: string; subtitulo: string } {
  if (etapa >= 3) return { titulo: 'Pedido entregue', subtitulo: 'Bom apetite! Obrigado pela preferência.' };
  if (etapa === 2) {
    return ehRetirada
      ? { titulo: 'Pronto para retirada', subtitulo: 'Seu pedido já está esperando por você na loja.' }
      : { titulo: 'Saiu para entrega', subtitulo: 'Seu pedido está a caminho.' };
  }
  return { titulo: 'Pedido confirmado', subtitulo: 'A loja recebeu e já está preparando seu pedido.' };
}

/** Moldura única da espera e do confirmado: mesmo tamanho para a troca de SVG não "saltar". */
function AnimacaoStatus({ src, alt }: { src: string; alt: string }): JSX.Element {
  return (
    <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box component="img" src={src} alt={alt} sx={{ width: 200, maxWidth: '100%', height: 'auto' }} />
    </Box>
  );
}

/** Anel fecha em verde e a comanda vira o check (toca uma vez ao montar, embutido no SVG). */
function IconeConfirmado(): JSX.Element {
  return <AnimacaoStatus src="/images/confirmacao-concluida.svg" alt="" />;
}

/** Topo da tela: ícone, título e uma frase curta sobre o momento do pedido. */
function HeroStatus({
  icone,
  titulo,
  subtitulo,
  tom,
}: {
  icone: ReactNode;
  titulo: string;
  subtitulo: string;
  tom?: 'warning' | 'error';
}): JSX.Element {
  return (
    <Stack alignItems="center" spacing={1} sx={{ textAlign: 'center', pt: 1 }}>
      {tom ? (
        <Box
          sx={(t) => ({
            width: 88,
            height: 88,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            color: `${tom}.main`,
            bgcolor: alpha(t.palette[tom].main, 0.12),
          })}
          aria-hidden
        >
          {icone}
        </Box>
      ) : (
        icone
      )}
      <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.25 }}>
        {titulo}
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 360 }}>
        {subtitulo}
      </Typography>
    </Stack>
  );
}

/** Número do pedido + modalidade, discretos logo abaixo do topo. */
function IdentificacaoPedido({ referencia, modalidade }: { referencia?: string; modalidade: string }): JSX.Element {
  return (
    <Stack direction="row" justifyContent="center" spacing={1} alignItems="center">
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          borderRadius: 999,
          bgcolor: 'action.hover',
          typography: 'body2',
          fontWeight: 700,
          letterSpacing: 0.5,
        }}
      >
        Pedido #{referencia}
      </Box>
      <Typography variant="body2" color="text.secondary">
        {modalidade}
      </Typography>
    </Stack>
  );
}

/**
 * Tela de espera enquanto o restaurante ainda não confirmou o pedido. O cliente
 * permanece aqui (com polling ativo) até a situação avançar para CONFIRMADO.
 */
function AguardandoConfirmacao(): JSX.Element {
  return (
    <Stack alignItems="center" spacing={1} sx={{ textAlign: 'center', pt: 1 }}>
      <AnimacaoStatus src="/images/aguardando-confirmacao.svg" alt="Aguardando confirmação do restaurante" />
      <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.25 }}>
        Aguardando a loja confirmar
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 340 }}>
        Seu pedido chegou ao restaurante. Avisaremos aqui assim que ele for confirmado.
      </Typography>
    </Stack>
  );
}

/** Bloco com borda suave e título opcional — agrupa as informações do pedido. */
function Secao({ titulo, children }: { titulo?: string; children: ReactNode }): JSX.Element {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>

      {titulo && (
        <Typography variant="body1" sx={{ display: 'block', lineHeight: 1.5, mb: 1.5, fontWeight: 'bold' }}>
          {titulo}
        </Typography>
      )}

      {children}
    </Paper>
  );
}

/** Convite para avaliar — só aparece depois que a loja confirmou o pedido. */
function ConviteAvaliacao({ aoAvaliar }: { aoAvaliar: () => void }): JSX.Element {
  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        p: 2,
        borderRadius: 3,
        borderColor: alpha(t.palette.primary.main, 0.3),
        bgcolor: alpha(t.palette.primary.main, 0.04),
      })}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.25} sx={{ color: '#FFC107', mb: 0.5 }} aria-hidden>
            {[0, 1, 2, 3, 4].map((i) => (
              <StarIcon key={i} fontSize="small" />
            ))}
          </Stack>
          <Typography fontWeight={700}>Como foi seu pedido?</Typography>
          <Typography variant="body2" color="text.secondary">
            Sua opinião ajuda a loja a melhorar.
          </Typography>
        </Box>
        <Button variant="contained" onClick={aoAvaliar} sx={{ flexShrink: 0 }}>
          Avaliar
        </Button>
      </Stack>
    </Paper>
  );
}

/** Chamada para o Pix direto à loja; QR Code e instruções ficam no modal. */
function ConvitePix({ valor, aoAbrir }: { valor: number; aoAbrir: () => void }): JSX.Element {
  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        p: 2,
        borderRadius: 3,
        borderColor: alpha(t.palette.success.main, 0.35),
        bgcolor: alpha(t.palette.success.main, 0.08),
      })}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography fontWeight={700}>Pague {formatarBRL(valor)} com Pix</Typography>
          <Typography variant="body2" color="text.secondary">
            Depois, envie o comprovante para a loja.
          </Typography>
        </Box>
        <Button variant="contained" color="success" onClick={aoAbrir} startIcon={<QrCode2Icon />} sx={{ flexShrink: 0 }}>
          Pagar
        </Button>
      </Stack>
    </Paper>
  );
}

function LinhaValor({
  rotulo,
  valor,
  destaque,
}: {
  rotulo: string;
  valor: string | JSX.Element
  destaque?: boolean;
}): JSX.Element {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
      <Typography
        variant={destaque ? 'subtitle1' : 'body1'}
        fontWeight={destaque ? 700 : 400}
        color={destaque ? 'text.primary' : 'text.secondary'}
      >
        {rotulo}
      </Typography>

      <Typography variant={destaque ? 'subtitle1' : 'body2'} fontWeight={destaque ? 700 : 500} sx={{ textAlign: 'right' }}>
        {valor}
      </Typography>
    </Stack>
  );
}

/**
 * Botões de contato com a loja, exibidos quando o pedido não foi confirmado a
 * tempo (ou falhou). Só renderiza os canais que a loja configurou.
 */
function ContatoRestaurante({
  config,
  referencia,
}: {
  config: ConfiguracaoLoja | undefined;
  referencia?: string;
}): JSX.Element | null {
  const whatsapp = config?.whatsapp?.trim();
  const instagram = config?.instagram?.trim();
  if (!whatsapp && !instagram) return null;

  return (
    <Stack spacing={1.5} sx={{ mb: 3 }}>
      <Typography align="center" color="text.secondary">
        Fale com o restaurante
      </Typography>
      {whatsapp && (
        <Button
          variant="contained"
          color="success"
          fullWidth
          startIcon={<WhatsAppIcon />}
          href={montarLinkWhatsapp(whatsapp, referencia)}
          target="_blank"
          rel="noopener"
        >
          Falar no WhatsApp
        </Button>
      )}
      {instagram && (
        <Button
          variant="outlined"
          fullWidth
          startIcon={<InstagramIcon />}
          href={montarLinkInstagram(instagram)}
          target="_blank"
          rel="noopener"
        >
          Abrir Instagram
        </Button>
      )}
    </Stack>
  );
}

/**
 * Acompanhamento vertical: "Recebido → Confirmado → Enviado/Aguardando retirada →
 * Entregue". Etapa feita ganha check; a atual fica em destaque; as próximas, apagadas.
 */
function LinhaDoTempo({ etapa, ehRetirada }: { etapa: number; ehRetirada: boolean }): JSX.Element {
  const etapas = rotulosEtapas(ehRetirada);
  return (
    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {etapas.map((nome, i) => {
        const feita = i <= etapa;
        const atual = i === etapa;
        const ultima = i === etapas.length - 1;
        return (
          <Box component="li" key={nome} sx={{ display: 'flex', gap: 1.5 }} aria-current={atual ? 'step' : undefined}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={(t) => ({
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: feita ? 'success.main' : 'text.disabled',
                  border: feita ? 'none' : `2px solid ${t.palette.divider}`,
                  boxShadow: atual ? `0 0 0 4px ${alpha(t.palette.success.main, 0.18)}` : 'none',
                })}
              >
                {feita && <CheckCircleIcon sx={{ fontSize: 22 }} />}
              </Box>
              {!ultima && (
                <Box
                  sx={{
                    width: 2,
                    flexGrow: 1,
                    minHeight: 18,
                    my: 0.5,
                    borderRadius: 1,
                    bgcolor: i < etapa ? 'success.main' : 'divider',
                  }}
                />
              )}
            </Box>
            <Typography
              variant="body2"
              color={feita ? 'text.primary' : 'text.disabled'}
              fontWeight={atual ? 700 : 400}
              sx={{ pb: ultima ? 0 : 2, lineHeight: '22px' }}
            >
              {nome}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function InfoLinha({ icone, titulo, texto }: { icone: ReactNode; titulo: string; texto?: string }): JSX.Element {

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 2,
          bgcolor: 'action.hover',
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icone}
      </Box>

      <Box sx={{ minWidth: 0, pt: texto ? 0 : 0.75 }}>
        <Typography variant="body1" fontWeight={600}>
          {titulo}
        </Typography>

        {texto && (
          <Typography variant="body2" color="text.secondary">
            {texto}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
